import { useRef, useState } from 'react'
import { validateMailReturnTo } from 'utils/mailComposeNavigation'
import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { useMailComposition, emptyMailContent } from 'contexts/MailCompositionContext'
import { useUser } from 'contexts/UserContext'
import { emptyRecipientEditor } from './MailRecipientInput'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from 'components/ui/alert-dialog'
export function MailCompositionNavigationGuard() {
  const session = useMailComposition()!
  const { user } = useUser()
  const exitLock = useRef(false), [exiting, setExiting] = useState(false)
  const buffers = Object.values(session.recipientEditors).some(editor => !!editor.buffer)
  const pending = session.busy || session.autosave.saving || !!session.pending
  const unsafe = !!user?.enabled_modules?.emails && (session.uploads.blocked || session.dirty || buffers || pending || !!session.autosave.recovery)
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    const internalTarget = session.internalNavigationTarget.current
    if (internalTarget) {
      session.internalNavigationTarget.current = null
      if (nextLocation.pathname + nextLocation.search === internalTarget) return false
    }
    if (!unsafe || nextLocation.pathname === '/login') return false
    if (nextLocation.pathname === currentLocation.pathname && nextLocation.search === currentLocation.search) return false
    const params = new URLSearchParams(nextLocation.search)
    // Serializing the current saved identity is part of the same editing session.
    const sameSaved = nextLocation.pathname === '/mail/compose' && session.draft?.id && params.get('draft') === session.draft.id
    const sameSource = nextLocation.pathname === '/mail/compose' && session.inlineTarget && params.get('message') === session.content.reply_to && params.get('mode') === session.content.mode && params.get('mailbox') === session.boxId
    const sameReturn = session.inlineTarget && session.returnTo && validateMailReturnTo(nextLocation.pathname + nextLocation.search) === validateMailReturnTo(session.returnTo)
    if (currentLocation.pathname === '/mail/compose' && nextLocation.pathname === '/mail/compose' && (sameSaved || sameSource)) return false
    if (!session.busy && !session.pending && (sameSaved || sameSource || sameReturn)) return false
    return true
  })
  useEffect(() => {
    if (blocker.state === 'blocked' && !unsafe && !exitLock.current) blocker.reset()
  }, [blocker.state, unsafe])
  useEffect(() => {
    if (!unsafe) return
    const warn = (event: BeforeUnloadEvent) => { if (!localStorage.getItem('accessToken')) return; event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [unsafe])
  const abandon = async () => {
    if (pending || blocker.state !== 'blocked' || exitLock.current) return
    exitLock.current = true; setExiting(true)
    try {
      const cancelled = await Promise.all(session.uploads.items.filter(item => item.status !== 'completed').map(item => session.uploads.remove(item.id)))
      if (cancelled.some(result => result === false)) return
      session.resetComposition()
      blocker.proceed()
    } finally { exitLock.current = false; setExiting(false) }
  }
  return <AlertDialog open={blocker.state === 'blocked'} onOpenChange={open => { if (!open && !exitLock.current && blocker.state === 'blocked') blocker.reset() }}>
    <AlertDialogContent><AlertDialogTitle>Modifiche da salvare</AlertDialogTitle>
      <AlertDialogDescription>{pending ? 'Attendi il salvataggio o la verifica dell’invio prima di uscire.' : session.autosave.recovery ? 'Il salvataggio richiede una verifica. Uscendo perderai il contenuto locale.' : buffers ? 'Ci sono destinatari non confermati. Il testo locale non è ancora salvato.' : session.autosave.status || 'Il contenuto locale non è ancora salvato.'} Le bozze e i documenti già salvati restano disponibili.</AlertDialogDescription>
      <AlertDialogFooter><AlertDialogCancel disabled={exiting} onClick={() => { if (blocker.state === 'blocked') blocker.reset() }}>Resta</AlertDialogCancel>
        {!pending && <AlertDialogAction disabled={exiting} onClick={abandon}>Esci senza salvare</AlertDialogAction>}
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
}
